import React, { useState, useEffect } from "react";
import {
   Row,
   Col,
   Badge,
   Form,
   Tab,
   Nav,
   Container,
   Card,
   Spinner,
   Table,
   OverlayTrigger,
   Tooltip
} from "react-bootstrap";
import Swal from 'sweetalert2';
import { MdOutlinePhone, MdOutlineMailOutline, MdLocationOn } from "react-icons/md";
import IconCalendar from '../../images/icon_primary_calendar.png';
import IconUsers from '../../images/icon_primary_users.png';
import IconAmount from '../../images/icon_primary_amount.png';
import IconCard from '../../images/icon_primary_card.png';
import ImageEmptyData from '../../images/image-empty-data.png';
import IconPhone from '../../images/icon-phone.png';
import IconLocation from '../../images/icon-location.png';
import IconEnvelop from '../../images/icon-envelop.png';
import './index.scss';

interface Address {
   street: string;
   city: string;
   state: string;
   zip: string;
}

interface CurrentPlan {
   name: string;
   duration: string;
   maxUsers: number;
   amount: string;
   paymentType: string;
   cardNumber: string;
}

interface PaymentHistory {
   id: string;
   date: string;
   endDate: string;
   amount: string;
   paymentMethod: string;
   maxUsers: number;
   discount?: string;
}

// Updated interface for assigned users from API
interface AssignedUser {
   memberId: string;
   firstName: string;
   lastName: string;
   email: string;
   active: boolean;
   role: string;
}

interface User {
   id: string;
   name: string;
   accountType: "Owner" | "Member";
   email: string;
   phone: string;
   address?: Address;
   registeredOn: string;
   expiresOn: string;
   status: "Active" | "Inactive" | "Blocked";
   avatar: string;
   currentPlan?: CurrentPlan;
   assignedUsers?: AssignedUser[];
   paymentHistory?: PaymentHistory[];
   subscriptionOwnerName?: string; // Added for "Added by" functionality
}

interface UserDetailsComponentProps {
   user: User;
   onBlockUser: (userId: string, isBlocked: boolean) => void;
   blockLoading?: boolean;
   onFetchPaymentHistory?: () => Promise<void>;
   paymentHistoryLoading?: boolean;
}

const UserDetailsComponent: React.FC<UserDetailsComponentProps> = ({ 
   user, 
   onBlockUser, 
   blockLoading = false,
   onFetchPaymentHistory,
   paymentHistoryLoading = false
}) => {
   const [activeTab, setActiveTab] = useState<string>('current-plan');
   const [paymentHistoryFetched, setPaymentHistoryFetched] = useState<boolean>(false);

   const handleBlockToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
       if (blockLoading) return;
       
       const newStatus = e.target.checked;
       const action = newStatus ? 'block' : 'unblock';
       
       const result = await Swal.fire({
           title: `Are you sure?`,
           text: `You are about to ${action} this user.`,
           icon: 'warning',
           showCancelButton: true,
           confirmButtonColor: '#28a745',
           cancelButtonColor: '#4D5E80',
           confirmButtonText: `Yes, ${action} user!`
       });

       if (result.isConfirmed) {
           onBlockUser(user.id, newStatus);
       } else {
           // Reset the toggle if user cancels
           e.target.checked = !newStatus;
       }
   };

   // Fetch payment history when payment history tab is selected
   useEffect(() => {
      if (activeTab === 'payment-history' && !paymentHistoryFetched && onFetchPaymentHistory) {
         onFetchPaymentHistory();
         setPaymentHistoryFetched(true);
      }
   }, [activeTab, paymentHistoryFetched, onFetchPaymentHistory]);

   const renderPaymentHistoryContent = () => {
      if (paymentHistoryLoading) {
         return (
            <div className="text-center py-5">
               <Spinner animation="border" variant="primary" />
               <p className="mt-3">Loading payment history...</p>
            </div>
         );
      }

      if (!user.paymentHistory || user.paymentHistory.length === 0) {
         return (
            <div className="text-center py-5">
               <div className="mb-3">
                  <i className="far fa-credit-card fa-3x text-muted"></i>
               </div>
               <p className="text-muted">No payment history found for this user.</p>
            </div>
         );
      }

      return (
         <div className="payment-history">
            {user.paymentHistory.map((payment) => (
               <Row key={payment.id} className="p-2 custom-list">
                  <Col md={5} className="list py-3">
                     <p className="mb-3 gilroy-semibold light">ID#: {payment.id}</p>
                     <p className="mb-0 gilroy-semibold light2">Date: <span className="text-light gilroy-semibold">{payment.date} - {payment.endDate}</span></p>
                  </Col>
                  <Col md={5} className="list py-3">
                     <div className="d-flex ">
                        <p className="mb-3 gilroy-semibold light2">Payment: <span className="text-light gilroy-semibold">{payment.paymentMethod}</span></p>
                     </div>
                     <p className="mb-0 gilroy-semibold light2">Max Users: <span className="text-light gilroy-semibold">{payment.maxUsers}</span></p>
                  </Col>
                  <Col md={2} className="list py-3">
                     <h4 className="mb-0" style={{ textAlign: 'end' }}>{payment.amount}</h4>
                  </Col>
               </Row>
            ))}
         </div>
      );
   };

   // Function to render the plan details based on user role
   const renderPlanDetails = () => {
      if (!user.currentPlan) return null;

      const isOwner = user.accountType === "Owner";
      
      return (
         <div className="highlights">
            <div className="d-flex justify-content-between bg-primary rounded p-3 mb-4">
               <h4 className="my-auto text-white">{user.currentPlan.name}</h4>
               {/* Only show "Added by" for Members */}
               {!isOwner && user.subscriptionOwnerName && (
                  <div className="d-flex">
                     <span className="my-auto me-3" style={{ color: '#C4FFE4' }}>Added by</span>
                     <h6 className="px-2 py-1 mb-0" style={{ background: '#C4FFE4', borderRadius: '16px', color: '#383838' }}>
                        {user.subscriptionOwnerName}
                     </h6>
                  </div>
               )}
            </div>

            <Row className="mb-4">
               <Col md={isOwner ? 6 : 6} className="mb-3 mb-md-0">
                  <div className="d-flex">
                     <img src={IconCalendar} alt="icon" />
                     <div className="my-auto ms-2">
                        <span className="gilroy-semibold" style={{ color: '#7D8FB3' }}>Duration</span>
                        <p className="mb-0 gilroy-semibold text-dark">{user.currentPlan.duration}</p>
                     </div>
                  </div>
               </Col>
               <Col md={isOwner ? 6 : 6}>
                  <div className="d-flex">
                     <img src={IconUsers} alt="icon" />
                     <div className="my-auto ms-2">
                        <span className="gilroy-semibold" style={{ color: '#7D8FB3' }}>Max Users</span>
                        <p className="mb-0 gilroy-semibold text-dark">{user.currentPlan.maxUsers}</p>
                     </div>
                  </div>
               </Col>
            </Row>

            {/* Only show Amount and Payment Type for Owners */}
            {isOwner && (
               <Row>
                  <Col md={6} className="mb-3 mb-md-0">
                     <div className="d-flex">
                        <img src={IconAmount} alt="icon" />
                        <div className="my-auto ms-2">
                           <span className="gilroy-semibold" style={{ color: '#7D8FB3' }}>Amount</span>
                           <p className="mb-0 gilroy-semibold text-dark">{user.currentPlan.amount}</p>
                        </div>
                     </div>
                  </Col>
                  <Col md={6}>
                     <div className="d-flex">
                        <img src={IconCard} alt="icon" />
                        <div className="my-auto ms-2">
                           <span className="gilroy-semibold" style={{ color: '#7D8FB3' }}>Payment Type</span>
                           <p className="mb-0 gilroy-semibold text-dark">
                              {user.currentPlan.paymentType} {user.currentPlan.cardNumber}
                           </p>
                        </div>
                     </div>
                  </Col>
               </Row>
            )}
         </div>
      );
   };

   // Format US phone number as (XXX)-XXX-XXXX
   function formatUSPhone(phone: string): string {
      const cleaned = ('' + phone).replace(/\D/g, '');
      if (cleaned.length === 10) {
        return `(${cleaned.slice(0,3)})-${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
      }
      return phone;
    }

   // Truncate email for display
   const truncateEmail = (email: string, maxLength: number = 20): string => {
      if (email.length <= maxLength) return email;
      
      const atIndex = email.indexOf('@');
      if (atIndex === -1) return email;
      
      const localPart = email.substring(0, atIndex);
      const domainPart = email.substring(atIndex);
      
      if (localPart.length > maxLength - domainPart.length - 3) {
         const truncatedLocal = localPart.substring(0, maxLength - domainPart.length - 3);
         return `${truncatedLocal}...${domainPart}`;
      }
      
      return email;
   };

   return (
      <Container fluid className="p-0 user-details-page mb-5">
         <Row>
            <Col xl={3}   md={12}>
               <Card className="h-100 px-0 px-md-4 py-0 py-md-5 border-0">
                  <div className="text-center mb-4">
                     <img
                        src={user.avatar || "/assets/avatars/default.jpg"}
                        alt={user.name}
                        width="100"
                        height="100"
                        className="rounded-circle mb-3"
                        onError={(e) => {
                           e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=random&color=fff&size=128`;
                        }}
                     />
                     <h4 className="mb-2 text-info">{user.name}</h4>
                     <p className="text-muted mb-2">{user.accountType}</p>

                     <Badge
                        bg={
                           user.status === "Active" ? "success" :
                              user.status === "Inactive" ? "secondary" :
                                 "danger"
                        }
                        className="px-3 py-2"
                        style={{ borderRadius: '32px', fontSize: '16px' }}
                     >
                        {user.status}
                     </Badge>
                  </div>

                  <div className="user-contact-info">
                     <div className="d-flex align-items-center mb-3">
                        <img src={IconPhone} alt="icon-phone" className="me-2" />
                        {user.phone ? (
                           <a 
                              href={`tel:${user.phone}`} 
                              className="text-info text-decoration-none"
                              style={{ cursor: 'pointer' }}
                           >
                              {formatUSPhone(user.phone)}
                           </a>
                        ) : (
                           <span className="text-muted">N/A</span>
                        )}
                        </div>

                     <hr />
                     <div className="d-flex align-items-center mb-3 user-details-email">
                        <img src={IconEnvelop} alt="icon-envelope" className="me-2" />
                        <OverlayTrigger
                           placement="top"
                           overlay={
                              <Tooltip id="email-tooltip">
                                 {user.email}
                              </Tooltip>
                           }
                        >
                           <a 
                              href={`mailto:${user.email}`} 
                              className="text-info text-decoration-none"
                              style={{ cursor: 'pointer' }}
                           >
                              {truncateEmail(user.email)}
                           </a>
                        </OverlayTrigger>
                     </div>
                     <hr />
                     {user.address && (
                        <>
                           <div className="d-flex align-items-center mb-3">
                              <img src={IconLocation} alt="icon-location" className="me-2" />
                              <span className="text-info">
                                 {user.address.street}, {user.address.city}, {user.address.state} - {user.address.zip}
                              </span>
                           </div>
                           <hr />
                        </>
                     )}
                     
                     <div className="mb-3 p-2" style={{ backgroundColor: '#F8F9FA' }}>
                        <small className="text-muted">Registered on:</small><br />
                        <strong>{user.registeredOn}</strong>
                     </div>

                     <div className="mb-3 p-2" style={{ backgroundColor: '#F8F9FA' }}>
                        <small className="text-muted">Expires on:</small><br />
                        <strong>{user.expiresOn}</strong>
                     </div>

                     <div className="block-toggle mt-4">
                        <div className="d-flex align-items-center">
                           <Form.Check
                              type="switch"
                              id="block-toggle"
                              label={user.status === "Blocked" ? "Unblock User" : "Block User"}
                              checked={user.status === "Blocked"}
                              onChange={handleBlockToggle}
                              disabled={blockLoading}
                           />
                           {blockLoading && (
                              <Spinner 
                                 animation="border" 
                                 size="sm" 
                                 className="ms-2"
                                 variant="primary"
                              />
                           )}
                        </div>
                        <small className="text-muted">
                           {user.status === "Blocked" 
                              ? "Toggle to unblock this user" 
                              : "Toggle to block this user"
                           }
                        </small>
                     </div>
                  </div>
               </Card>
            </Col>
            <Col xl={9} md={12}>
               <Card className="py-4 px-0 px-md-4 border-0 h-100">
                  {user.currentPlan ? (
                     <div className="user-plan-details">
                        <Tab.Container id="user-details-tabs" activeKey={activeTab} onSelect={(k) => k && setActiveTab(k)}>
                           <Nav variant="tabs" className="border-bottom mb-3">
                              <Nav.Item>
                                 <Nav.Link eventKey="current-plan">Current Active Plan</Nav.Link>
                              </Nav.Item>
                              <Nav.Item>
                                 <Nav.Link eventKey="payment-history">Payment History</Nav.Link>
                              </Nav.Item>
                           </Nav>
                           <Tab.Content className="">
                              <Tab.Pane eventKey="current-plan">
                                 {renderPlanDetails()}

                                 {/* Only show Assigned Users for Owners */}
                                 {user.accountType === "Owner" && user.assignedUsers && user.assignedUsers.length > 0 && (
                                    <div className="mt-4">
                                       <h3 className="mb-3 text-dark">Assigned Users ({user.assignedUsers.length})</h3>
                                       <hr />
                                       <div className="users-list">
                                          {user.assignedUsers.map((assignedUser, index) => (
                                             <div key={assignedUser.memberId || index} className="">
                                                <div className="d-flex justify-content-between align-items-center">
                                                   <div>
                                                      <p className="mb-0 text-dark gilroy-semibold" style={{ fontSize: '18px' }}>
                                                         {assignedUser.firstName} {assignedUser.lastName}
                                                      </p>
                                                      <OverlayTrigger
                                                         placement="top"
                                                         overlay={
                                                            <Tooltip id={`assigned-user-email-tooltip-${index}`}>
                                                               {assignedUser.email}
                                                            </Tooltip>
                                                         }
                                                      >
                                                         <small style={{ color: "#7D8FB3", cursor: 'help' }}>
                                                            {truncateEmail(assignedUser.email)}
                                                         </small>
                                                      </OverlayTrigger>
                                                   </div>
                                                   <div className="d-flex align-items-center">
                                                      <Badge 
                                                         bg={assignedUser.active ? "success" : "secondary"}
                                                         className="me-2"
                                                      >
                                                         {assignedUser.active ? "Active" : "Inactive"}
                                                      </Badge>
                                                      {/* <Badge 
                                                         bg="primary" 
                                                         className="text-capitalize"
                                                      >
                                                         {assignedUser.role}
                                                      </Badge> */}
                                                   </div>
                                                </div>
                                                {index < user.assignedUsers!.length - 1 && <hr />}
                                             </div>
                                          ))}
                                       </div>
                                    </div>
                                 )}

                                 {/* Show message when no assigned users for Owners */}
                                 {user.accountType === "Owner" && (!user.assignedUsers || user.assignedUsers.length === 0) && (
                                    <div className="mt-4 text-center py-4">
                                       <div className="mb-3">
                                          <i className="far fa-users fa-3x text-muted"></i>
                                       </div>
                                       <p className="text-muted">No assigned users found.</p>
                                    </div>
                                 )}
                              </Tab.Pane>

                              <Tab.Pane eventKey="payment-history">
                                 {renderPaymentHistoryContent()}
                              </Tab.Pane>
                           </Tab.Content>
                        </Tab.Container>
                     </div>
                  ) : (
                     <div className="text-center pt-5 h-100 my-auto">
                        <div className="mb-5">
                           <img
                              src={ImageEmptyData}
                              alt="No Plan"
                           />
                        </div>
                        <h4 style={{ color: '#7D8FB3' }} className="mb-4">The user is all set up!</h4>
                        <div className="w-75 mx-auto">
                           <p style={{ color: '#7D8FB3' }}>
                              Since they are new, there is no subscription or payment history yet. 
                              Once they subscribe, their details will appear here.
                           </p>
                        </div>
                     </div>
                  )}
               </Card>
            </Col>
         </Row>
      </Container>
   );
};

export default UserDetailsComponent;